import { i18n } from "@lingui/core"
import * as en from "~/locales/en/messages"
import * as ptBr from "~/locales/pt-br/messages"
import "./globals.css"
import { t } from "@lingui/core/macro"
import { type PropsWithChildren, Suspense, useEffect } from "react"
import {
  Links,
  type LinksFunction,
  type LoaderFunctionArgs,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  useLoaderData,
  useLocation,
  useMatches,
  useNavigation,
  useRouteLoaderData,
} from "react-router"
import { OnlyLogoLayout } from "~/components/layouts/only-logo/only-logo-layout"
import { AnimatedSmallLogo } from "~/components/ui"
import { ErrorPage } from "~/components/ui/error-page"
import { ScreenGuard } from "~/components/ui/screen-guard"
import { useErrorBoundaryData } from "~/hooks/use-error-boundary-data"
import type { loader as privateLoader } from "~/routes/_private/route"
import { env } from "~/utils/.server"
import { localeCookie } from "~/utils/.server/locale-cookie"
import type { PublicEnvsSchema } from "~/utils/env.types"
import { cn } from "~/utils/style"
import {
  DEFAULT_LOCALE,
  allLocales,
  isSupportedLocale,
} from "~/utils/supported-locales"
import "~/infra/typebox-setup"
import { I18nProvider } from "@lingui/react"
import { EnvsProvider } from "./components/contexts/envs-context"

i18n.load("en", en.messages)
i18n.load("pt-br", ptBr.messages)
i18n.activate(DEFAULT_LOCALE)

const ogImagePath = "/images/og-image.png"

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com" },
  {
    rel: "apple-touch-icon",
    sizes: "180x180",
    href: "/favicons/apple-touch-icon.png",
  },
  {
    rel: "icon",
    sizes: "32x32",
    href: "/favicons/favicon-32x32.png",
    type: "image/png",
  },
  {
    rel: "icon",
    sizes: "16x16",
    href: "/favicons/favicon-16x16.png",
    type: "image/png",
  },
  {
    rel: "icon",
    href: "/favicon.ico",
  },
]

export const meta: MetaFunction = () => {
  return [
    { title: "Loadpass." },
    {
      name: "description",
      content: "Secure B2B payment platform for importers & exporters",
    },
  ]
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url)
  const cookieValue = await localeCookie.parse(request.headers.get("Cookie"))
  const locale = isSupportedLocale(cookieValue) ? cookieValue : DEFAULT_LOCALE

  return {
    locale,
    ENV: {
      PUBLIC_BASE_URL_HOST: env("PUBLIC_BASE_URL_HOST"),
      PUBLIC_DASHBOARD_POLLING_INTERVAL: env(
        "PUBLIC_DASHBOARD_POLLING_INTERVAL",
      ),
      PUBLIC_CONVERSION_ORDER_STATUS_POLLING_INTERVAL: env(
        "PUBLIC_CONVERSION_ORDER_STATUS_POLLING_INTERVAL",
      ),
      PUBLIC_IDLE_INTERVAL_IN_MINUTES: env("PUBLIC_IDLE_INTERVAL_IN_MINUTES"),
      PUBLIC_POLLING_INTERVAL: env("PUBLIC_POLLING_INTERVAL"),
      PUBLIC_SENTRY_DSN: env("PUBLIC_SENTRY_DSN"),
      PUBLIC_TRANSACTION_MESSAGE_ADDRESS_SUFFIX: env(
        "PUBLIC_TRANSACTION_MESSAGE_ADDRESS_SUFFIX",
      ),
      PUBLIC_CONVERSION_ORDER_POLLING_INTERVAL: env(
        "PUBLIC_CONVERSION_ORDER_POLLING_INTERVAL",
      ),
    } satisfies Record<PublicEnvsSchema, string>,
    originURL: url.origin,
    url: url.href,
  }
}

export const Layout = ({ children }: PropsWithChildren) => {
  const loaderData = useRouteLoaderData<typeof loader>("root")
  const privateLoaderData =
    useRouteLoaderData<typeof privateLoader>("routes/_private")
  const navigation = useNavigation()

  const matches = useMatches()

  const isReceiptValidationPage = matches.find(
    match => match.id === "routes/receipt",
  )

  const activeLocale = loaderData?.locale ?? DEFAULT_LOCALE
  const htmlLang = allLocales()[activeLocale].htmlLang

  return (
    <html lang={htmlLang}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <Meta />

        <meta property="og:title" content="Loadpass" />
        <meta
          property="og:description"
          content="Secure B2B payment platform for importers & exporters"
        />

        <meta property="og:url" content={loaderData?.url} />
        <meta
          property="og:image"
          content={`${loaderData?.originURL}${ogImagePath}`}
        />
        <meta
          property="og:image:secure_url"
          content={`${loaderData?.originURL}${ogImagePath}`}
        />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="Loadpass" />
        <meta name="twitter:title" content="Loadpass" />
        <meta
          name="twitter:description"
          content="Secure B2B payment platform for importers & exporters"
        />
        <meta
          name="twitter:image"
          content={`${loaderData?.originURL}${ogImagePath}`}
        />
        <meta
          name="twitter:image:title"
          content="Loadpass - B2B payment platform"
        />
        <meta
          name="twitter:image:alt"
          content="Loadpass - B2B payment platform"
        />
        <meta name="twitter:image:width" content="1200" />
        <meta name="twitter:image:height" content="630" />
        <meta name="twitter:image:file_name" content="og-image.png" />
        <meta name="twitter:image:content_type" content="image/png" />

        {/*
          NOTE: O Suspense aqui é um workaround para que não dê erro de hydration
          no Cypress: https://github.com/cypress-io/cypress/issues/27204#issuecomment-2224833564
        */}
        <Suspense>
          <Links />
        </Suspense>
      </head>
      <body>
        {privateLoaderData?.shouldShowProductionGuard && <ProductionGuard />}

        <div
          className={cn(
            "hidden xl:block",
            !!isReceiptValidationPage && "block",
          )}
        >
          <div
            className={cn(
              navigation.state === "loading" && "opacity-60",
              "h-screen",
            )}
          >
            {children}
          </div>
          <AnimatedSmallLogo shouldShow={navigation.state === "loading"} />
        </div>

        <div
          className={cn(
            "block xl:hidden",
            !!isReceiptValidationPage && "hidden",
          )}
        >
          <ScreenGuard />
        </div>

        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(loaderData?.ENV || {})}`,
          }}
        />
        <Scripts />
      </body>
    </html>
  )
}

export default function App() {
  const { locale } = useLoaderData<typeof loader>()
  const location = useLocation()

  if (i18n.locale !== locale) {
    i18n.activate(locale)
  }

  useEffect(() => {
    const scrollAreaRef = document.querySelectorAll(
      "[data-radix-scroll-area-viewport]",
    )

    scrollAreaRef.forEach(el => {
      if (location.pathname && el instanceof HTMLElement) {
        el.scrollTop = 0
      }
    })
  }, [location.pathname])

  return (
    <I18nProvider i18n={i18n}>
      <Outlet />
    </I18nProvider>
  )
}

export function ErrorBoundary() {
  const rootLoaderData = useRouteLoaderData<typeof loader>("root")

  return (
    <EnvsProvider envs={rootLoaderData?.ENV}>
      <ErrorBoundaryContent />
    </EnvsProvider>
  )
}

function ErrorBoundaryContent() {
  const privateLoaderData =
    useRouteLoaderData<typeof privateLoader>("routes/_private")
  const { status, heading, description } = useErrorBoundaryData()

  const errorPage = (
    <ErrorPage status={status} heading={heading} description={description} />
  )

  if (privateLoaderData?.user.id) {
    return errorPage
  }

  return <OnlyLogoLayout>{errorPage}</OnlyLogoLayout>
}

function ProductionGuard() {
  return (
    <div
      className={cn(
        "py-2 px-6 text-white font-bold rounded-md text-xs uppercase",
        "overflow-hidden",
        "z-20 top-4 left-1/2 -translate-x-1/2",
        "bg-red-600 flex justify-center items-center absolute",
        "before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-linear-to-r before:from-transparent before:via-white/60 before:to-transparent",
      )}
    >
      {t`Be careful, you're in production environment`}
    </div>
  )
}
